import { Button, notification, Select } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as sillyname from 'sillyname';
import {
  IPartyForUser,
  IPartyStageForUser,
  IParticipantForUser,
  TAreaKey,
  EAreaPrefix,
  TUserId,
  StreamType,
} from '../../PartyClientResponse';
import { getAreaInfo, getNextRoomId } from '../../controller/Areas';
import { runDebugFunction, joinArea, pingParty } from '../../controller/client/PartyClient';
import { getIdToken } from '../../controller/PartyDB';
import { useVideoController } from '../../controller/video/VideoControlContext';
import { useFunctionsEmulatorOnLocal } from '../../firebase/firebase';
import { AppMapProps, IAppState } from '../../redux/reducer';
import { useAppState, ActionType } from '../../state/AppState';
import HomeButton from '../common/HomeButton';
import SelectAV from '../Onboarding';
import UserControls from '../UserControls';
import { PING_FREQUENCY, ZINDEX_UI, APP_VERSION, MAIN_AREA_HEIGHT } from './AppConstants';
import { MAX_PEERS } from '../../ApiConstants';
import CurrentArea from './CurrentArea';
import MainVideoPlayer from '../../components/video/MainVideoPlayer';
import OtherAreas from './OtherAreas';
import useResizeObserver from 'use-resize-observer';
import { ApolloClient, gql, useApolloClient } from '@apollo/client';
import DonateCTA from './DonateCTA';
import Cockpit from '../cockpit/Cockpit';
import PartyParticipantBox from './PartyParticipantBox';
import SpotlightedParticipant from './SpotlightedParticipant';
import firebase from 'firebase/app';
import StorageService from '../../services/StorageService';
import VolumeControls from './VolumeControls';
import { useLocation } from 'react-router-dom';
import IntroModal from './IntroModal';
import PartyExplorer from '../navigation/PartyExplorer';
import ShareScreen from '../../controller/video/ShareScreen';
import { MenuOutlined } from '@ant-design/icons';
import SideMenu from './SideMenu';
import InviteFriendsButton from './InviteFriendsButton';

const fakeUserStorageService = new StorageService();

export const DEMO_AREAS = [
  `${EAreaPrefix.ChatPrefix}-1237821`,
  `${EAreaPrefix.ChatPrefix}--10000101`,
  `${EAreaPrefix.ChatPrefix}-1023012`,
  `${EAreaPrefix.ChatPrefix}-1023011`,
  `${EAreaPrefix.ChatPrefix}-1023011`,
];

export async function joinNewArea(
  user: firebase.User | null,
  party: IPartyForUser,
  type: EAreaPrefix
) {
  await joinArea({
    idToken: await getIdToken(),
    key: party.key,
    area: getNextRoomId(type),
    name: `${user?.displayName}'s Room`,
  });
}

let fakeUserAvatars: string[] = [];

const getFakeUserAvatars = async (client: ApolloClient<object>) => {
  if (fakeUserAvatars.length === 0) {
    const resp = await client.query({
      query: gql`
        query GetAvatars {
          backroomAvatars {
            url
          }
        }
      `,
      variables: {},
    });
    const avatars = resp.data.backroomAvatars.map((a: { url: string }) => encodeURI(a.url));
    fakeUserAvatars = avatars;
    return avatars;
  }
  return fakeUserAvatars;
};

const generateFakeUser = async (
  fakeUserArea: string,
  client: ApolloClient<object>,
  properties: Partial<IParticipantForUser> = {}
): Promise<IParticipantForUser> => {
  try {
    const avatars = await getFakeUserAvatars(client);
    const fakeVideoUrl = await fakeUserStorageService.getRandomFakeVideoDownloadUrl();
    return {
      currentArea: fakeUserArea as TAreaKey,
      tags: [],
      uid: `${Math.round(Math.random() * 10000)}` as unknown as TUserId,
      displayName: sillyname(),
      photoURL: avatars[Math.round(Math.random() * avatars.length)],
      fakeVideoUrl,
      active: true,
      ...properties,
    };
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const _Stage = ({
  party,
  initialArea,
  stage,
  debug,
  features,
}: {
  party: IPartyForUser;
  initialArea: TAreaKey | null;
  stage: IPartyStageForUser;
  features: {
    roundAvatars: boolean;
    volumeControlTowardsPeer: boolean;
    volumeControlFromPeer: boolean;
  };
} & IAppState) => {
  useFunctionsEmulatorOnLocal();

  const {
    user,
    masterVolume,
    cockpitMode,
    fakeUsers,
    participants,
    participantsByArea,
    dispatch,
    currentArea,
    currentParticipant,
    isSharingScreen,
  } = useAppState();
  const {
    ref: wrapperRef,
    height: wrapperHeight = 0,
    width: wrapperWidth = 0,
  } = useResizeObserver<HTMLDivElement>();

  const [debugFuncName, setDebugFuncName] = useState<string>('populatePartyPreviews');

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();
  const bots = query.get('bots');

  const [currentAreaSizing, setCurrentAreaSizing] = useState<{
    width: number;
    height: number;
    offsetTop: number;
    offsetLeft: number;
  }>({
    width: 0,
    height: 0,
    offsetLeft: 0,
    offsetTop: 0,
  });

  const [localTimestamp, setLocalTimestamp] = useState<number>(0);

  const [visibleMenu, setVisibleMenu] = useState(false);

  /* Participant behavior */
  const [knownAreas, setKnownAreas] = useState<string[]>([]);
  useEffect(() => {
    if (initialArea && !knownAreas.includes(initialArea)) {
      // Only join once, even if the parameter stays
      setKnownAreas([...knownAreas, initialArea]);
      (async () => {
        // Let's call `joinArea` directly, to avoid hooks mayhem.
        joinArea({
          idToken: await getIdToken(),
          key: party.key,
          area: initialArea,
        });
      })();
    }
  }, [user, party, initialArea, knownAreas]);
  /* End participant behavior */

  // useSendThumbnail();

  const currentAreaFeatures = getAreaInfo(currentArea, party);

  const { getDebugInfo, connectToSession, setNeedsAVForArea } = useVideoController();

  useEffect(() => {
    setNeedsAVForArea(currentAreaFeatures.needsAV);
  }, [currentAreaFeatures.needsAV, setNeedsAVForArea]);

  const sessionId = party.stage.sessionId;
  const sessionToken = currentParticipant?.sessionToken;
  useEffect(() => {
    if (connectToSession) {
      connectToSession(sessionId, sessionToken);
    }
  }, [sessionId, sessionToken, connectToSession]);

  // Ping party every PING_FREQUENCY ms to let the backend know we are still here
  useEffect(() => {
    const interval: NodeJS.Timeout | -1 = user
      ? setInterval(() => {
          getIdToken()
            .then((idToken) => pingParty({ idToken, key: party.key }))
            .then()
            .catch((e) => console.error(e));
        }, PING_FREQUENCY * 1000)
      : -1;
    return () => {
      if (interval !== -1) {
        clearInterval(interval);
      }
    };
  }, [user, party.key, stage]);

  const client = useApolloClient();

  // preload bot avatars for my convenience
  const [avatarsLoaded, setAvatarsLoaded] = useState<boolean>(false);
  useEffect(() => {
    (async () => {
      if (client) {
        try {
          await getFakeUserAvatars(client);
          setAvatarsLoaded(true);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [client, setAvatarsLoaded]);

  // setup Demo users
  useEffect(() => {
    (async () => {
      if (!avatarsLoaded) return;

      if (party.slug === 'demo' || bots) {
        const areaNames = [...DEMO_AREAS];

        let NUM_BOTS = 4;
        if (bots) {
          NUM_BOTS = Number(bots);
        }

        let newFakeUserPromises = [];
        for (let i = 0; i < NUM_BOTS; i++) {
          newFakeUserPromises.push(
            generateFakeUser('', client, {
              currentArea: areaNames[i % 4] as EAreaPrefix,
            })
          );
        }
        const newFakeUsers = await Promise.all(newFakeUserPromises);
        dispatch({ type: ActionType.SetFakeUsers, fakeUsers: newFakeUsers });
      }
    })();
  }, [party.slug, client, avatarsLoaded, bots, dispatch]);

  // Randomly move a fake user every X seconds
  useEffect(() => {
    const MAX_DELAY_BETWEEN_MOVES_IN_MS = 15 * 1000;
    let timeout: NodeJS.Timeout;

    const moveFakeParticipant = () => {
      timeout = setTimeout(() => {
        dispatch({ type: ActionType.MoveRandomFakeUser });
        moveFakeParticipant();
      }, Math.random() * MAX_DELAY_BETWEEN_MOVES_IN_MS);
    };

    moveFakeParticipant();

    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch]);

  const moveToArea = useCallback(
    async (area: TAreaKey): Promise<void> => {
      setKnownAreas([...knownAreas, area]);
      const availableAreas = Object.keys(participantsByArea);
      const existingArea = availableAreas.includes(area);

      // Do nothing if clicking in my current area
      if (currentArea === area) {
        return;
      }

      // Joining an existing area
      if (existingArea) {
        const areaInfo = getAreaInfo(area, party);

        const userIsAllowed =
          user?.isAdmin ||
          (currentParticipant?.tags || []).includes('host') ||
          (currentParticipant?.tags || []).includes('moderator') ||
          !areaInfo.settings?.private ||
          !!areaInfo.settings?.allowedParticipants?.[user?.uid || ('' as TUserId)];

        if (!userIsAllowed) {
          return notification.open({
            message: 'Private room',
            description:
              'Sorry, this is a private room. Try joining a different one or creating your own!',
          });
        }

        const areaIsFull =
          !area.startsWith(EAreaPrefix.LobbyPrefix) && participantsByArea[area].length >= MAX_PEERS;
        if (areaIsFull) {
          return notification.open({
            message: 'Area full',
            description:
              'Sorry, this room is full. Try joining a different one or creating your own!',
          });
        } else {
          joinArea({
            idToken: await getIdToken(),
            key: party.key,
            area,
          });
          return;
        }
      }

      // On initial join - shouldn't select an area, just be put in one
      if (!currentArea) {
        const availableAreasOfType = Object.entries(participantsByArea).filter(
          (arr) => arr[1].length < MAX_PEERS && arr[0].startsWith(area)
        );

        // Area exists, let's join
        if (availableAreasOfType.length) {
          joinArea({
            idToken: await getIdToken(),
            key: party.key,
            area: availableAreasOfType[0][0] as EAreaPrefix,
          });
          return;
        } else {
          // Create a new area of given type
          joinArea({
            idToken: await getIdToken(),
            key: party.key,
            area: getNextRoomId(area),
            name: 'Welcome',
          });
          return;
        }
      }

      // Creating a new area of type
      try {
        return joinNewArea(user, party, area);
      } catch (e) {
        // Get here if modal canceled
        return;
      }
    },
    [currentParticipant?.tags, currentArea, knownAreas, participantsByArea, party, user]
  );

  let mainVideoHeight = window.innerHeight;
  if (stage.stream.type === StreamType.None) {
    mainVideoHeight = 0;
  }
  let mainVideoWidth = window.innerWidth;

  const { [currentArea]: currentAreaParticipants, ...otherAreaParticipants } = participantsByArea;

  const addFakeUser = useCallback(() => {
    (async () => {
      console.log('calling add fake user');
      const newFakeUser = await generateFakeUser(currentParticipant?.currentArea || '', client);
      console.log(newFakeUser);
      dispatch({ type: ActionType.SetFakeUsers, fakeUsers: [...fakeUsers, newFakeUser] });
    })();
  }, [currentParticipant?.currentArea, client, fakeUsers, dispatch]);

  if (!currentParticipant) {
    return <div>Loading...</div>;
  }

  if (cockpitMode) {
    return <Cockpit party={party} />;
  }

  return (
    <div id='wrapper'>
      {/* <IntroTour /> */}
      <MainVideoPlayer
        setLocalTimestamp={setLocalTimestamp}
        key='twitch'
        height={mainVideoHeight}
        width={mainVideoWidth}
        stage={stage}
        volume={(currentAreaFeatures?.streamVolume || 1) * masterVolume}
      />
      <IntroModal />
      <div className='container'>
        <div className='left'>
          <div className='layout-left-top'>
            <div style={{ height: 60 }}>
              <MenuOutlined
                style={{ fontSize: 24, cursor: 'pointer' }}
                onClick={() => setVisibleMenu(true)}
              />
              <SideMenu visible={visibleMenu} onClose={() => setVisibleMenu(false)} />
              {isSharingScreen && <ShareScreen />}
            </div>
            <InviteFriendsButton />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column-reverse',
                flexGrow: 1,
                pointerEvents: 'none',
              }}>
              {/* {!!stage.spotlight?.participant.uid && (
                <div
                  // onClick={async () => {
                  //   if (stage.spotlight?.participant.uid) {
                  //     highlightParticipant({
                  //       idToken: await getIdToken(),
                  //       key: party.key,
                  //       participant: null,
                  //     });
                  //   }
                  // }}
                  style={{
                    // top: 40,
                    // left: 40,
                    width: 200,
                    height: 200,
                    zIndex: 1,
                    // position: 'fixed',
                    transition: 'all 0.5s',
                    borderRadius: '50%',
                    // padding: 2,
                    // background: 'black',
                  }}>
                </div>
              )} */}
              <SpotlightedParticipant party={party} height={200} width={200} audioEnabled />
            </div>
          </div>
          <div className='layout-left-bottom'>
            <OtherAreas
              party={party}
              stage={stage}
              moveToArea={moveToArea}
              participantsByArea={otherAreaParticipants}
              currentParticipantArea={currentArea}
              currentAreaSize={currentAreaSizing}
            />
          </div>
          {/* <EnableAV /> */}
        </div>
        <div className='center' ref={wrapperRef}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              alignItems: 'center',
            }}>
            <PartyExplorer />
            <div className='main-video-container'></div>
            <div
              className='my-dance-spot'
              style={{ maxHeight: !!mainVideoHeight ? MAIN_AREA_HEIGHT : 'none' }}>
              <CurrentArea
                participants={currentAreaParticipants}
                currentParticipant={currentParticipant}
                party={party}
                allParticipants={participants}
                setSizing={setCurrentAreaSizing}
              />
            </div>
          </div>
        </div>
        <div className='right'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '20%',
            }}>
            <UserControls
              stage={stage}
              party={party}
              localTimestamp={localTimestamp}
              debug={debug}
              addFakeUser={addFakeUser}
            />
          </div>
          <div
            style={{
              height: '80%',
              display: 'flex',
              flexDirection: 'column',
              // justifyContent: 'flex-end',
            }}>
            <DonateCTA party={party} />
            <VolumeControls />
            <PartyParticipantBox party={party} />
          </div>
        </div>
      </div>
      <div
        style={{
          zIndex: ZINDEX_UI,
          position: 'absolute',
          bottom: 20,
          left: 20,
        }}>
        <SelectAV />
      </div>
      {debug && (
        <div
          className='api-version'
          style={{
            opacity: 0.8,
          }}>
          <div style={{ padding: 12 }}>{`APP VERSION: ${APP_VERSION}`}</div>
          <div style={{ padding: 12 }}>
            <Button
              style={{ marginRight: 12 }}
              onClick={async () => {
                runDebugFunction({ idToken: await getIdToken(), funcName: debugFuncName });
              }}>
              run
            </Button>
            <Select value={debugFuncName} onSelect={setDebugFuncName}>
              {[
                {
                  name: 'broadcastAllPartiesUpdates',
                  args: {},
                },
                {
                  name: 'populatePartyPreviews',
                  args: {},
                },
              ].map((func) => (
                <Select.Option
                  id={`debug-${func.name}`}
                  data-args={JSON.stringify(func.args)}
                  key={func.name}
                  value={func.name}>
                  {func.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div style={{ padding: 12 }}>
            <Button
              onClick={() =>
                dispatch({ type: ActionType.SetUserDevAdmin, isAdmin: !user?.isAdmin })
              }>
              {user?.isAdmin ? 'turn off admin mode' : 'turn on admin mode'}
            </Button>
          </div>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{`userId: ${user?.uid}`}</pre>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(
              { partyKey: party.key, muteAllParticipants: party.stage.muteAllParticipants },
              null,
              2
            )}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(currentParticipant, null, 2)}
          </pre>
          {Object.entries(getDebugInfo(party, currentParticipant) || {}).map((x) => (
            <div key={x[0]}>
              <div>{x[0]}</div>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{String(x[1])}</pre>
            </div>
          ))}
        </div>
      )}
      {/* <div
        className='api-version'
        style={{
          pointerEvents: 'none',

          left: 0,
          top: 'initial',
          bottom: 0,
          ...(debug ? { opacity: 1, backgroundColor: 'red' } : { opacity: 0 }),
        }}>
        {JSON.stringify({ features })}
      </div> */}
    </div>
  );
};
const Stage = connect(AppMapProps)(_Stage);

export default Stage;
